import React, { useState } from 'react';
import { ArrowClockwise, Crown, EnvelopeSimple, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

export const AI_SUBSCRIPTION_EMAIL = 'hello@galago.in';

const contactHref = (featureName) => {
  const subject = 'GalaGo AI Premium subscription';
  const body = `Hello GalaGo team,\n\nI would like to subscribe to ${featureName || 'GalaGo AI Premium features'}.\n\nPlease share the next steps.`;
  return `mailto:${AI_SUBSCRIPTION_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export function AIPremiumDialog({
  open,
  onOpenChange,
  featureName = 'this AI feature',
  onManualEntry,
  manualEntryLabel = 'Enter details manually',
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-amber-200 p-0 sm:max-w-md">
        <div className="bg-gradient-to-br from-amber-50 via-white to-violet-50 px-6 pb-5 pt-7">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Crown weight="fill" className="h-6 w-6" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl">Subscribe for advanced AI features</DialogTitle>
            <DialogDescription className="pt-2 leading-6">
              {featureName} is part of GalaGo AI Premium. Contact the developer at{' '}
              <span className="font-medium text-foreground">{AI_SUBSCRIPTION_EMAIL}</span> to activate a subscription for your account.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 rounded-lg border border-amber-200/80 bg-white/80 p-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Sparkle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" weight="fill" />
              <p>Online subscription checkout is coming soon. For now, we’ll help you subscribe over email.</p>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 px-6 pb-6 sm:justify-stretch">
          {onManualEntry && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                onManualEntry();
              }}
            >
              {manualEntryLabel}
            </Button>
          )}
          <Button asChild className="w-full bg-amber-600 text-white hover:bg-amber-700">
            <a href={contactHref(featureName)}>
              <EnvelopeSimple className="h-4 w-4" />
              Contact developer
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AIPremiumPageWall({
  featureName = 'this AI feature',
  onRefresh,
  refreshing = false,
}) {
  return (
    <div className="mx-auto flex min-h-[65vh] w-full max-w-2xl items-center justify-center px-4 py-10">
      <div className="w-full overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-violet-50 p-7 text-center shadow-sm sm:p-10">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Crown weight="fill" className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-2xl font-semibold text-foreground">Subscribe for advanced AI features</h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-muted-foreground">
          {featureName} is part of GalaGo AI Premium. Contact the developer at{' '}
          <span className="font-medium text-foreground">{AI_SUBSCRIPTION_EMAIL}</span> to activate access for your account.
        </p>
        <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
          <Button asChild className="bg-amber-600 text-white hover:bg-amber-700">
            <a href={contactHref(featureName)}>
              <EnvelopeSimple className="h-4 w-4" />
              Contact developer
            </a>
          </Button>
          {onRefresh && (
            <Button type="button" variant="outline" onClick={onRefresh} disabled={refreshing}>
              <ArrowClockwise className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Checking access' : 'Check access again'}
            </Button>
          )}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Online subscription checkout is coming soon.</p>
      </div>
    </div>
  );
}

export default function AIPremiumButton({
  featureName,
  children,
  className,
  onManualEntry,
  manualEntryLabel,
  onClick: _onClick,
  disabled: _disabled,
  type = 'button',
  ...buttonProps
}) {
  const [open, setOpen] = useState(false);
  const { hasAiPremiumAccess, aiSubscriptionLoading } = useAuth();

  return (
    <>
      <Button
        type={type}
        className={cn('group relative', className)}
        disabled={_disabled || aiSubscriptionLoading}
        onClick={(event) => {
          if (hasAiPremiumAccess) {
            _onClick?.(event);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        aria-label={typeof children === 'string' ? children : 'Subscription feature'}
        title="Subscription feature"
        {...buttonProps}
      >
        <Crown className="h-4 w-4 text-amber-500" weight="fill" />
        {children}
      </Button>
      <AIPremiumDialog
        open={open}
        onOpenChange={setOpen}
        featureName={featureName}
        onManualEntry={onManualEntry}
        manualEntryLabel={manualEntryLabel}
      />
    </>
  );
}
